import { motion } from "framer-motion";
import { MapPin, Briefcase, ArrowRight } from "lucide-react";
import { JOBS } from "@/data/content";

function applyMailto(job) {
  const subject = encodeURIComponent(`Application for ${job.title} — TriNexa`);
  const body = encodeURIComponent(
    `Hi TriNexa team,\n\nI'd like to apply for the ${job.title} role (${job.department}, ${job.location}).\n\nPlease find my background below / attached.\n\nThank you,\n`
  );
  return `mailto:hr@tri-nexa.com?subject=${subject}&body=${body}`;
}

export default function Careers() {
  return (
    <div data-testid="careers-page" className="relative">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 grid-bg radial-fade opacity-60 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 relative">
          <p className="font-mono-tech text-xs tracking-[0.3em] uppercase text-[#00D4FF] mb-4">
            Careers
          </p>
          <h1 className="font-display text-5xl sm:text-6xl font-bold tracking-tight text-white max-w-3xl leading-[1.05]">
            Build the <span className="text-gradient-cyan">future of software</span> with us.
          </h1>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl leading-relaxed">
            We hire deeply curious engineers, designers and operators who care
            about craft, outcomes and the people they work with.
          </p>
        </div>
      </section>

      {/* JOBS LIST */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="space-y-4">
            {JOBS.map((job, i) => (
              <motion.div
                key={job.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.04 }}
                data-testid={`job-card-${job.slug}`}
                className="group p-6 md:p-7 rounded-2xl bg-[#0A1128] border border-white/10 hover-glow"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#00D4FF]/10 text-[#00D4FF] text-[11px] font-mono-tech tracking-widest">
                        <Briefcase size={12} /> {job.department}
                      </span>
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-white/10 text-slate-300 text-[11px]">
                        <MapPin size={12} /> {job.location}
                      </span>
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-white/10 text-slate-300 text-[11px]">
                        {job.type}
                      </span>
                    </div>
                    <h3 className="font-display text-2xl font-bold text-white">
                      {job.title}
                    </h3>
                    <p className="text-sm text-slate-400 max-w-2xl">
                      {job.summary}
                    </p>
                  </div>
                  <a
                    href={applyMailto(job)}
                    data-testid={`apply-btn-${job.slug}`}
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#00D4FF] text-[#050B14] font-semibold hover:bg-[#33DDFF] transition shrink-0"
                  >
                    Apply now <ArrowRight size={16} />
                  </a>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-12 p-8 rounded-2xl bg-[#0A1128] border border-[#00D4FF]/20 text-center">
            <h3 className="font-display text-2xl font-bold text-white">
              Don't see the right role?
            </h3>
            <p className="mt-2 text-slate-400">
              We're always interested in great engineers and operators. Send us
              a note.
            </p>
            <a
              href="mailto:hr@tri-nexa.com?subject=General%20Application%20%E2%80%94%20TriNexa"
              data-testid="careers-general-apply"
              className="mt-5 inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-[#00D4FF] text-[#00D4FF] hover:bg-[#00D4FF] hover:text-[#050B14] transition font-semibold"
            >
              Send your resume
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
